import moment from 'moment';
import { formatMoney } from '../../../utils/formatMoney';
import { CARD_BRAND_LABELS, normalizeCardBrand } from '../views/ManageMembership/utils';

/**
 * Membership renewal messaging — one source of truth for every surface that talks
 * about a failing, ending or ended membership (Figma "Renewal Flow": Home sheet,
 * Account banner + header, Manage membership).
 *
 * Driven by GET /subscriptions/renewal-status. Everything here is pure so the copy
 * can be tested without rendering.
 *
 * Copy follows the Figma except where the Figma contradicts the shipped policy:
 * the D2 reversal (2026-09-15) SUSPENDS bag registration during the grace period,
 * so grace copy never says "you can still register and ship bags".
 *
 * Offers are optional by design. The backend offer config is seeded disabled
 * until pricing signs off (PRD O2), so every surface has a no-offer variant and
 * never prints a dollar amount the API did not send.
 */

export const RENEWAL_STATES = Object.freeze({
  ACTIVE: 'ACTIVE',
  ENDING: 'ENDING',
  PAST_DUE: 'PAST_DUE',
  LAPSED: 'LAPSED',
  NONE: 'NONE',
});

// Which billing rail a membership is on (renewal-status `provider`).
export const RENEWAL_PROVIDERS = Object.freeze({
  STRIPE: 'STRIPE',
  SHOPIFY: 'SHOPIFY',
});

export const RENEWAL_ACTIONS = Object.freeze({
  UPDATE_PAYMENT: 'UPDATE_PAYMENT',
  RENEW: 'RENEW',
  RESUME: 'RESUME',
});

// Outcome of the renewal charge the API retries when a past-due Stripe member saves
// a card: `data.retry.status` on PATCH /subscriptions/:id/update. Mirrors the API's
// PAYMENT_UPDATE_RETRY_STATUS. A member in good standing gets SKIPPED.
export const PAYMENT_UPDATE_RETRY_STATUS = Object.freeze({
  PAID: 'paid',
  DECLINED: 'declined',
  REQUIRES_ACTION: 'requires_action',
  PENDING: 'pending',
  FAILED: 'failed',
  SKIPPED: 'skipped',
});

// Why a SKIPPED retry did not charge. Mirrors the API's PAYMENT_UPDATE_RETRY_SKIP_REASON;
// only the reasons where a past-due member's new card waits for the scheduled retry
// are listed (not_past_due / auto_renew_off mean there was nothing to charge).
export const PAYMENT_UPDATE_RETRY_SKIP_REASON = Object.freeze({
  RATE_LIMITED: 'rate_limited',
  ATTEMPT_IN_FLIGHT: 'attempt_in_flight',
  LOCK_UNAVAILABLE: 'lock_unavailable',
  NO_OPEN_INVOICE: 'no_open_invoice',
});

/**
 * Whether a SKIPPED retry left a past-due member's new card waiting for the next
 * scheduled attempt (as opposed to there being nothing to charge).
 *
 * @param {object|null} retry - `data.retry` from the card update
 * @return {boolean}
 */
export const isRetryDeferred = (retry) => retry?.status === PAYMENT_UPDATE_RETRY_STATUS.SKIPPED
  && Object.values(PAYMENT_UPDATE_RETRY_SKIP_REASON).includes(retry.reason);

// What became of the card a past-due Shopify member changed on Shopify's page:
// renewal-status `paymentUpdate.status`. Mirrors the API's PAYMENT_UPDATE_STATUS.
export const PAYMENT_UPDATE_STATUS = Object.freeze({
  PENDING: 'pending',
  DECLINED: 'declined',
  SKIPPED: 'skipped',
});

/**
 * A past-due member whose new card is being charged right now (Shopify, seconds to
 * a minute after they saved it). Still paused until it lands, but not "failed".
 *
 * @param {object} status
 * @return {boolean}
 */
export const isPaymentProcessing = (status) => status?.state === RENEWAL_STATES.PAST_DUE
  && status.paymentUpdate?.status === PAYMENT_UPDATE_STATUS.PENDING;

/**
 * A past-due member whose new card is saved but waits for the next scheduled attempt
 * (retry limit, or an attempt on the previous card was already in flight).
 *
 * @param {object} status
 * @return {boolean}
 */
export const isPaymentMethodSaved = (status) => status?.state === RENEWAL_STATES.PAST_DUE
  && status.paymentUpdate?.status === PAYMENT_UPDATE_STATUS.SKIPPED;

/**
 * The membership is paid up again. Not just "no longer past due": a grace period that
 * ran out (LAPSED), or anything else a past-due member can move to, is not a renewal.
 * A renewal leaves them ACTIVE (auto-renew is on for anyone in dunning).
 *
 * @param {object|null} status
 * @return {boolean}
 */
export const isRenewed = (status) => status?.state === RENEWAL_STATES.ACTIVE;

/**
 * "Request a new link in N seconds", singular at 1.
 *
 * @param {number} seconds
 * @return {string}
 */
export const resendCountdownLabel = (seconds) => `Request a new link in ${seconds} ${
  seconds === 1 ? 'second' : 'seconds'}`;

// What a "turn auto-renewal back on" button says while the API call runs.
export const RESUME_PENDING_LABEL = 'Turning on…';
// What a "renew" button says while the renewal checkout is created (then Shopify).
export const RENEW_PENDING_LABEL = 'Taking you to checkout…';
// …and for a lapsed Stripe member, who may renew on their saved card instead: the
// app is checking that before it knows which way they go.
export const RENEW_CHECKING_PENDING_LABEL = 'One moment…';
// What "Update payment method" says while a Shopify member's emailed link is requested.
export const UPDATE_PAYMENT_PENDING_LABEL = 'Sending your link…';

// What a card save says while it runs, step by step. A past-due member's save also
// charges the renewal, which takes a few seconds more.
export const CARD_SAVE_STEP = Object.freeze({
  SAVING: 'SAVING',
  RENEWING: 'RENEWING',
});
// "Payment method": the payment form also saves non-card methods. "Updating", not
// "renewing": the API may leave the new method for the next attempt (retry limit, an
// attempt already in flight), which the result then says.
export const CARD_SAVE_STEP_LABEL = Object.freeze({
  [CARD_SAVE_STEP.SAVING]: 'Saving your payment method…',
  [CARD_SAVE_STEP.RENEWING]: 'Updating your membership…',
});
// The payment form's Save button while Stripe confirms (and runs the bank's check).
export const PAYMENT_METHOD_SAVING_LABEL = 'Saving…';

// Back from the bank's confirmation page: while the payment lands, and if it doesn't.
export const BANK_PAYMENT_CHECKING_LABEL = 'Checking your payment…';
export const BANK_PAYMENT_PENDING_MESSAGE = 'We haven\'t heard from your bank yet. It can take a few '
  + 'minutes; this page updates when it does. If you didn\'t finish confirming, you can open the '
  + 'page again below.';

// A lapsed Stripe member renewing on the card Stripe already holds (the API's
// /subscriptions/winback/stripe-* endpoints). What the confirmation sheet's buttons do.
export const STRIPE_WINBACK_ACTIONS = Object.freeze({
  CONFIRM: 'STRIPE_WINBACK_CONFIRM',
  AUTHENTICATE: 'STRIPE_WINBACK_AUTHENTICATE',
  CHECK_AGAIN: 'STRIPE_WINBACK_CHECK_AGAIN',
  // Continue to the Shopify renewal checkout (a declined card, or "use a different card").
  SHOPIFY_CHECKOUT: 'STRIPE_WINBACK_SHOPIFY_CHECKOUT',
  DONE: 'STRIPE_WINBACK_DONE',
});

// Which screen of the sheet is showing.
export const STRIPE_WINBACK_PHASE = Object.freeze({
  CONFIRM: 'CONFIRM',
  PAID: 'PAID',
  DECLINED: 'DECLINED',
  REQUIRES_ACTION: 'REQUIRES_ACTION',
  FAILED: 'FAILED',
});

export const STRIPE_WINBACK_CONFIRM_PENDING_LABEL = 'Renewing your membership…';
export const STRIPE_WINBACK_CHECK_AGAIN_PENDING_LABEL = 'Checking…';

/**
 * What every Renew CTA says while it runs. A lapsed Stripe member may renew on their
 * saved card instead of going to checkout, and the app is checking which.
 *
 * @param {object|null} status - renewal-status payload
 * @return {string}
 */
export const getRenewPendingLabel = (status) => (
  status?.state === RENEWAL_STATES.LAPSED && status.provider === RENEWAL_PROVIDERS.STRIPE ?
    RENEW_CHECKING_PENDING_LABEL :
    RENEW_PENDING_LABEL
);

/**
 * A renewal CTA's label while its action is in flight, or null to keep its own.
 *
 * @param {string} action - RENEWAL_ACTIONS or STRIPE_WINBACK_ACTIONS value
 * @param {object|null} [status] - renewal-status payload (Renew's label depends on it)
 * @return {string|null}
 */
export const getPendingCtaLabel = (action, status = null) => ({
  [RENEWAL_ACTIONS.RESUME]: RESUME_PENDING_LABEL,
  [RENEWAL_ACTIONS.RENEW]: getRenewPendingLabel(status),
  [RENEWAL_ACTIONS.UPDATE_PAYMENT]: UPDATE_PAYMENT_PENDING_LABEL,
  [STRIPE_WINBACK_ACTIONS.CONFIRM]: STRIPE_WINBACK_CONFIRM_PENDING_LABEL,
  [STRIPE_WINBACK_ACTIONS.CHECK_AGAIN]: STRIPE_WINBACK_CHECK_AGAIN_PENDING_LABEL,
  [STRIPE_WINBACK_ACTIONS.SHOPIFY_CHECKOUT]: RENEW_PENDING_LABEL,
})[action] || null;

export const RENEWAL_ILLUSTRATIONS = Object.freeze({
  BROKEN_CARD: 'BROKEN_CARD',
  CLOSETS: 'CLOSETS',
  CAT: 'CAT',
});

export const RENEWAL_TONES = Object.freeze({
  ALERT: 'ALERT',
  OK: 'OK',
});

export const formatLongDate = (date) => (date ? moment(date).format('MMMM D, YYYY') : null);
export const formatShortDate = (date) => (date ? moment(date).format('M/D/YY') : null);

/**
 * "$10" for whole dollars, "$9.50" otherwise — the Figma never shows "$10.00".
 *
 * @param {number|null} cents
 * @return {string|null}
 */
export const formatCents = (cents) => {
  if (!Number.isFinite(cents)) return null;
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : formatMoney(dollars);
};

const pluralDays = (days) => `${days} ${days === 1 ? 'day' : 'days'}`;
const hasDays = (days) => Number.isFinite(days) && days >= 0;

// "Offer expires in N days" (Figma, updated 2026-10-02). Null when the offer has
// no expiry configured — the window length is pending (PRD O7).
const expiryLabelOf = (offer) => (
  offer?.daysRemaining ? `Offer expires in ${pluralDays(offer.daysRemaining)}` : null
);

const offerOf = (status) => {
  const offer = status?.offer;
  if (!offer || !Number.isFinite(offer.amountOffCents)) return null;
  return {
    off: formatCents(offer.amountOffCents),
    regular: formatCents(offer.regularAmountCents),
    discounted: formatCents(offer.offerAmountCents),
    daysRemaining: Number.isFinite(offer.daysRemaining) ? offer.daysRemaining : null,
  };
};

export const isOrderingPaused = (status) => status?.state === RENEWAL_STATES.PAST_DUE;

/**
 * "Visa •••• 4242" — named as Manage membership names it — or the wallet ("Apple
 * Pay") for a wallet card without digits, or "your card" when Stripe did not say.
 *
 * @param {object|null} card - quote `card`, the same shape as renewal-status
 *   `paymentDetails`: { brand, last4, label }
 * @return {string}
 */
export const formatCardLabel = (card) => {
  if (!card?.last4) return card?.label || 'your card';
  const brand = CARD_BRAND_LABELS[normalizeCardBrand(card.brand)] || 'Card';
  return `${brand} •••• ${card.last4}`;
};

/**
 * The win-back sheet for a lapsed Stripe member renewing on their saved card, one
 * screen per phase. Pure: the provider owns the calls and the phase.
 *
 *   CONFIRM          the card and what is charged today (with the offer and tax),
 *                    before anything is charged; "Use a different card" → Shopify
 *   PAID             the membership is active again
 *   DECLINED         the card on file was declined → the Shopify checkout (product:
 *                    ask for payment details only when the saved card fails)
 *   REQUIRES_ACTION  the bank wants to confirm: Stripe's page in a new tab, then
 *                    this tab notices the renewal by itself
 *   FAILED           the outcome is unknown (a timeout): check before trying again
 *
 * @param {object} params
 * @param {string} params.phase - STRIPE_WINBACK_PHASE value
 * @param {object} params.quote - GET /subscriptions/winback/stripe-quote
 * @param {object|null} [params.result] - POST /subscriptions/winback/stripe-renew
 * @param {object|null} [params.renewalWatch] - the provider's watch while the bank
 *   page is open: `{ timedOut }`
 * @return {object} RenewalSheet content
 */
export const getStripeWinbackSheetContent = ({
  phase, quote, result = null, renewalWatch = null,
}) => {
  const card = formatCardLabel(quote?.card);
  const total = formatCents(quote?.totalCents);
  const regular = formatCents(quote?.regularAmountCents);
  const off = quote?.discountCents > 0 ? formatCents(quote.discountCents) : null;

  switch (phase) {
    case STRIPE_WINBACK_PHASE.PAID: {
      const renewsOn = formatLongDate(result?.renewsAt || quote?.renewsAt);
      return {
        title: 'Welcome back to Unlimited!',
        body: `Your membership is active again${renewsOn ? ` until ${renewsOn}` : ''}. `
          + `We charged ${card}${total ? ` ${total}` : ''}.`,
        ctaLabel: 'Done',
        action: STRIPE_WINBACK_ACTIONS.DONE,
      };
    }
    case STRIPE_WINBACK_PHASE.DECLINED:
      return {
        title: 'Your card on file was declined',
        body: `${result?.message ? `${result.message} ` : ''}You can renew with another card at checkout`
          + `${off ? ` — your ${off} offer still applies` : ''}.`,
        ctaLabel: 'Continue to checkout',
        dismissLabel: 'Not now',
        action: STRIPE_WINBACK_ACTIONS.SHOPIFY_CHECKOUT,
      };
    case STRIPE_WINBACK_PHASE.REQUIRES_ACTION: {
      let footnote = null;
      if (renewalWatch?.timedOut) footnote = BANK_PAYMENT_PENDING_MESSAGE;
      else if (renewalWatch) footnote = BANK_PAYMENT_CHECKING_LABEL;
      return {
        title: 'Confirm with your bank',
        body: 'Your bank needs you to confirm this payment. We’ll open a secure Stripe page in a new tab — '
          + 'come back here when you’re done and we’ll finish your renewal.',
        ctaLabel: renewalWatch ? 'Open the payment page again' : 'Confirm payment',
        footnote,
        secondaryLabel: 'Use a different card',
        secondaryAction: STRIPE_WINBACK_ACTIONS.SHOPIFY_CHECKOUT,
        action: STRIPE_WINBACK_ACTIONS.AUTHENTICATE,
      };
    }
    case STRIPE_WINBACK_PHASE.FAILED: {
      // Nothing attempted yet: the API said a renewal may already be going through.
      if (result?.awaitingRenewal) {
        return {
          title: 'We’re checking on your renewal',
          body: 'It looks like your renewal may already be going through. Check again in a moment — '
            + 'you won’t be charged twice.',
          ctaLabel: 'Check again',
          dismissLabel: 'Not now',
          action: STRIPE_WINBACK_ACTIONS.CHECK_AGAIN,
        };
      }
      return {
        title: 'We couldn’t confirm your renewal',
        body: `${result?.message ? `${result.message} ` : 'Something went wrong while renewing your membership. '}`
          + 'Check again in a moment — you won’t be charged twice.',
        ctaLabel: 'Check again',
        dismissLabel: 'Not now',
        action: STRIPE_WINBACK_ACTIONS.CHECK_AGAIN,
      };
    }
    case STRIPE_WINBACK_PHASE.CONFIRM:
    default: {
      const summary = [
        { label: 'Payment method', value: card },
        { label: 'Unlimited membership, 1 year', value: regular },
        off && { label: 'Your offer', value: `−${off}` },
        quote?.taxCents > 0 && { label: 'Tax', value: formatCents(quote.taxCents) },
        { label: 'Total today', value: total, emphasis: true },
      ].filter((line) => line && line.value);
      return {
        title: 'Renew your Unlimited membership',
        body: `We’ll charge ${card} on file — no need to enter your card again.`,
        summary,
        // Two lines (design review, 2026-10-20).
        footnote: regular ?
          `Renews yearly at ${regular} plus tax.\nYou can turn off auto-renewal anytime.` :
          'Renews yearly.\nYou can turn off auto-renewal anytime.',
        ctaLabel: total ? `Renew for ${total}` : 'Renew now',
        secondaryLabel: 'Use a different card',
        secondaryAction: STRIPE_WINBACK_ACTIONS.SHOPIFY_CHECKOUT,
        action: STRIPE_WINBACK_ACTIONS.CONFIRM,
      };
    }
  }
};

/**
 * Whether pre-expiry messaging is due. Auto-renew-ON members get nothing (D5); an
 * auto-renew-OFF member is messaged only inside the configured window.
 *
 * @param {object} status
 * @return {boolean}
 */
const isEndingSoon = (status) => (
  status?.state === RENEWAL_STATES.ENDING && !!status.isInPreExpiryWindow
);

const PAUSED_BODY = 'Until then your membership benefits are paused, so you can’t register or order bags.';

/**
 * The sheet shown once on entry (Figma "Home – … banner"). Null when nothing is due.
 *
 * @param {object} status - renewal-status payload
 * @return {object|null}
 */
export const getEntrySheetContent = (status) => {
  const offer = offerOf(status);

  // Their new card is being charged, or saved for the next attempt: "we couldn't
  // renew, update your payment method" would contradict what they just did.
  if (isPaymentProcessing(status) || isPaymentMethodSaved(status)) return null;

  if (status?.state === RENEWAL_STATES.PAST_DUE) {
    const graceEnds = formatLongDate(status.graceEndsAt);
    // "card" only for a card; Shop Pay, PayPal, Link… are a payment method.
    const method = status.paymentDetails?.last4 ? 'your card' : 'your payment method';
    return {
      illustration: RENEWAL_ILLUSTRATIONS.BROKEN_CARD,
      title: 'We couldn’t renew your Unlimited membership',
      body: graceEnds ?
        `We’ll keep retrying ${method} until ${graceEnds}. ${PAUSED_BODY}` :
        `We’ll keep retrying ${method} for a few more days. ${PAUSED_BODY}`,
      ctaLabel: 'Update payment method',
      dismissLabel: null,
      action: RENEWAL_ACTIONS.UPDATE_PAYMENT,
    };
  }

  if (status?.state === RENEWAL_STATES.LAPSED) {
    if (offer) {
      return {
        illustration: RENEWAL_ILLUSTRATIONS.CLOSETS,
        showWordmark: true,
        title: `Get ${offer.off} Off\nTrashie Unlimited`,
        body: `Renew your Trashie Unlimited Membership and save ${offer.off} on your next year of decluttering`,
        ctaLabel: `Renew and save ${offer.off}`,
        // The design replaces "Maybe later" with the countdown; the close button
        // still dismisses. Without an expiry, keep the dismiss link.
        footnote: expiryLabelOf(offer),
        dismissLabel: expiryLabelOf(offer) ? null : 'Maybe later',
        action: RENEWAL_ACTIONS.RENEW,
      };
    }
    // No live offer (none issued, or it expired): no sheet at all. The member
    // renews from Account's "Become a member again" (Figma 12155:4507).
    return null;
  }

  if (isEndingSoon(status)) {
    const endsOn = formatLongDate(status.endsAt);
    const endsLine = endsOn ? `Your membership ends on ${endsOn}.` : 'Your membership ends soon.';
    return {
      illustration: RENEWAL_ILLUSTRATIONS.CLOSETS,
      showWordmark: true,
      title: offer ? `Save ${offer.off} on your\nnext year of Unlimited` : 'Keep your Unlimited membership',
      body: offer ?
        `${endsLine}\nTurn auto-renewal back on and save ${offer.off} on next year’s membership.` :
        `${endsLine}\nTurn auto-renewal back on to keep it going without interruption.`,
      ctaLabel: 'Turn Auto Renew Back On',
      dismissLabel: 'Not now',
      action: RENEWAL_ACTIONS.RESUME,
    };
  }

  return null;
};

/**
 * "Your membership is paused" — shown when a past_due member tries to order.
 * Verbatim from the Figma; it already matches the D2 reversal.
 */
export const PAUSED_SHEET_CONTENT = Object.freeze({
  illustration: null,
  title: 'Your membership is paused',
  body: 'Your renewal payment didn’t go through. Update your payment method to reactivate your membership '
    + 'and get back to registering, shipping, and ordering bags.',
  ctaLabel: 'Update payment method',
  dismissLabel: 'Not now',
  action: RENEWAL_ACTIONS.UPDATE_PAYMENT,
});

/**
 * Shopify members cannot sign in to Shopify, so updating the card means Shopify
 * emails them a secure link. Verbatim from the Figma.
 */
export const SHOPIFY_EMAIL_SHEET_CONTENT = Object.freeze({
  title: 'Check your inbox',
  body: 'Your membership is billed through Shopify, so we’ve just emailed you a secure link to update your '
    + 'payment method. It might take a minute to arrive.',
  // Past due only: saving the card renews them straight away, and Shopify's page
  // has no way back here.
  pastDueBody: 'Your membership is billed through Shopify, so we’ve just emailed you a secure link to update '
    + 'your payment method. It might take a minute to arrive. Once you’ve updated it, come back here — we’ll '
    + 'renew your membership right away.',
  resendLabel: 'Resend email',
  dismissLabel: 'Got it',
});

// The kinds of "after the emailed link" sheet; see getShopifyPaymentSheet.
export const SHOPIFY_PAYMENT_SHEET_KIND = Object.freeze({
  EMAIL: 'email',
  PROCESSING: 'processing',
  RENEWED: 'renewed',
  DECLINED: 'declined',
  SAVED: 'saved',
});

/**
 * What the Shopify payment sheet shows. It opens as "Check your inbox"; once the
 * member is back from the emailed link, renewal-status says what their new card did,
 * and "Resend email" would be wrong for every outcome but "not updated yet".
 *
 * Only a sheet opened while past due changes: a member in good standing changing
 * their card is never charged for it, so there is nothing to report. The outcome the
 * member was already looking at when they asked for a link (say, the decline that
 * made them ask for another one) is not news: only a different one moves the sheet
 * on. Compared by identity, not by time, so a skewed clock cannot misread it.
 *
 * @param {object|null} status - renewal-status payload
 * @param {{wasPastDue: boolean, seenUpdateAt: string|null}} sheet - how it was opened,
 *   and the `paymentUpdate.at` already on screen then
 * @return {{kind: string, content: object}}
 */
export const getShopifyPaymentSheet = (status, { wasPastDue, seenUpdateAt }) => {
  const email = {
    kind: SHOPIFY_PAYMENT_SHEET_KIND.EMAIL,
    content: {
      title: SHOPIFY_EMAIL_SHEET_CONTENT.title,
      body: wasPastDue ? SHOPIFY_EMAIL_SHEET_CONTENT.pastDueBody : SHOPIFY_EMAIL_SHEET_CONTENT.body,
      ctaLabel: SHOPIFY_EMAIL_SHEET_CONTENT.resendLabel,
      dismissLabel: SHOPIFY_EMAIL_SHEET_CONTENT.dismissLabel,
      action: RENEWAL_ACTIONS.UPDATE_PAYMENT,
    },
  };
  // Unknown (unreadable) status: keep what the member already sees.
  if (!wasPastDue || !status) return email;

  if (isRenewed(status)) {
    return {
      kind: SHOPIFY_PAYMENT_SHEET_KIND.RENEWED,
      content: {
        title: 'You’re all set',
        body: 'Your payment method is updated and your Unlimited membership is renewed.',
        ctaLabel: 'Done',
      },
    };
  }

  // Ended while the sheet was open (or anything else unexpected): no claim either way.
  if (status.state !== RENEWAL_STATES.PAST_DUE) return email;

  const update = status.paymentUpdate;
  if (!update?.at || update.at === seenUpdateAt) return email;

  switch (update.status) {
    case PAYMENT_UPDATE_STATUS.PENDING:
      return {
        kind: SHOPIFY_PAYMENT_SHEET_KIND.PROCESSING,
        content: {
          title: 'Processing your payment',
          body: 'Your new payment method is saved. We’re renewing your Unlimited membership with it — this '
            + 'usually takes less than a minute.',
          ctaLabel: 'Got it',
        },
      };
    case PAYMENT_UPDATE_STATUS.DECLINED:
      return {
        kind: SHOPIFY_PAYMENT_SHEET_KIND.DECLINED,
        content: {
          title: 'That payment method didn’t go through',
          body: 'We couldn’t renew your membership with the payment method you just saved. Try a different '
            + 'one — we’ll email you a new secure link.',
          ctaLabel: 'Update payment method',
          dismissLabel: 'Not now',
          action: RENEWAL_ACTIONS.UPDATE_PAYMENT,
        },
      };
    case PAYMENT_UPDATE_STATUS.SKIPPED: {
      const next = formatLongDate(status.nextRetryAt);
      return {
        kind: SHOPIFY_PAYMENT_SHEET_KIND.SAVED,
        content: {
          title: 'Your payment method is saved',
          body: `We’ll use it for your next renewal attempt${next ? ` on ${next}` : ''}. Your benefits unlock `
            + 'as soon as the payment goes through.',
          ctaLabel: 'Got it',
        },
      };
    }
    default:
      return email;
  }
};

/**
 * The pink card on the Account page, above Refer a Friend. Null when nothing is due.
 *
 * @param {object} status
 * @return {object|null}
 */
export const getAccountBannerContent = (status) => {
  const offer = offerOf(status);

  if (isPaymentProcessing(status)) {
    return {
      illustration: RENEWAL_ILLUSTRATIONS.BROKEN_CARD,
      title: 'Processing your payment',
      body: 'We’re renewing your membership with your new payment method. Your benefits unlock as soon as it '
        + 'goes through.',
      ctaLabel: null,
      action: null,
    };
  }

  if (isPaymentMethodSaved(status)) {
    const next = formatLongDate(status.nextRetryAt);
    return {
      illustration: RENEWAL_ILLUSTRATIONS.BROKEN_CARD,
      title: 'Payment method saved',
      body: `We’ll use your new payment method for your next renewal attempt${next ? ` on ${next}` : ''}. Your `
        + 'benefits unlock as soon as it goes through.',
      ctaLabel: null,
      action: null,
    };
  }

  if (status?.state === RENEWAL_STATES.PAST_DUE) {
    return {
      illustration: RENEWAL_ILLUSTRATIONS.BROKEN_CARD,
      title: 'Payment failed.',
      body: 'We couldn’t process your renewal payment. Your benefits unlock again as soon as your payment goes through.',
      ctaLabel: 'Update Payment Method',
      action: RENEWAL_ACTIONS.UPDATE_PAYMENT,
    };
  }

  if (status?.state === RENEWAL_STATES.LAPSED) {
    // Only while a win-back is live; without one, Account shows no banner
    // (Figma 12155:4507) and the member renews from "Become a member again".
    if (!offer) return null;
    return {
      illustration: RENEWAL_ILLUSTRATIONS.CAT,
      title: 'Membership Expired!',
      body: `Renew your Trashie Unlimited Membership and save ${offer.off} on your next year of decluttering`,
      ctaLabel: `Renew and save ${offer.off}`,
      footnote: expiryLabelOf(offer),
      action: RENEWAL_ACTIONS.RENEW,
    };
  }

  if (isEndingSoon(status)) {
    return {
      illustration: RENEWAL_ILLUSTRATIONS.CAT,
      title: hasDays(status.daysRemaining) ?
        `Your membership\nends in ${pluralDays(status.daysRemaining)}` :
        'Your membership\nends soon',
      body: 'Set up auto renew today to avoid any interruptions in your service.',
      ctaLabel: offer ? `Renew and save ${offer.off}` : 'Turn on auto-renew',
      action: RENEWAL_ACTIONS.RESUME,
    };
  }

  return null;
};

/**
 * The two-line status under the Account header's member meta. Null for ACTIVE and
 * NONE, which keep the existing "Unlimited Member" label.
 *
 * @param {object} status
 * @return {{primary: string, secondary: string, tone: string}|null}
 */
export const getAccountStatusLabel = (status) => {
  switch (status?.state) {
    case RENEWAL_STATES.PAST_DUE:
      if (isPaymentProcessing(status)) {
        return { primary: 'Processing payment', secondary: 'Renewal in progress', tone: RENEWAL_TONES.OK };
      }
      if (isPaymentMethodSaved(status)) {
        return {
          primary: 'Payment method saved',
          secondary: status.nextRetryAt ? `Retrying ${formatShortDate(status.nextRetryAt)}` : 'Past due',
          tone: RENEWAL_TONES.ALERT,
        };
      }
      return { primary: 'Payment failed', secondary: 'Past due', tone: RENEWAL_TONES.ALERT };
    case RENEWAL_STATES.LAPSED:
      // Without a live win-back the header is the plain "Active N mos." (Figma
      // 12155:4507), not an ended status.
      if (!offerOf(status)) return null;
      return {
        primary: status.endedAt ? `Ended ${formatShortDate(status.endedAt)}` : 'Ended',
        secondary: 'Membership ended',
        tone: RENEWAL_TONES.ALERT,
      };
    case RENEWAL_STATES.ENDING:
      return {
        primary: 'Auto-renew off',
        secondary: status.endsAt ? `Ends ${formatShortDate(status.endsAt)}` : 'Ending soon',
        tone: RENEWAL_TONES.OK,
      };
    default:
      return null;
  }
};

/**
 * Manage membership copy per state (Figma "Manage membership – …"). Null for
 * LAPSED too: Manage never renders it, it sends the member to the renewal checkout.
 *
 * @param {object} status
 * @return {object}
 */
export const getManageMembershipContent = (status) => {
  const offer = offerOf(status);
  const amount = formatCents(status?.amountCents);

  switch (status?.state) {
    case RENEWAL_STATES.PAST_DUE: {
      const last4 = status.paymentDetails?.last4;
      const graceEnds = formatLongDate(status.graceEndsAt);
      const renewal = formatLongDate(status.renewalDate);
      const card = last4 ? `your card ending in **${last4}` : 'your payment method';
      const update = status.paymentUpdate?.status;
      if (update === PAYMENT_UPDATE_STATUS.PENDING) {
        return {
          statusLabel: 'Processing payment',
          tone: RENEWAL_TONES.OK,
          summary: `We’re renewing your ${amount ? `${amount} ` : ''}Unlimited membership with your new payment `
            + 'method.',
          detailTitle: 'Processing payment',
          detailBody: 'This usually takes less than a minute. Registering and ordering bags unlock as soon as '
            + 'it goes through.',
          footnote: null,
          autoRenewOn: true,
        };
      }
      const next = formatLongDate(status.nextRetryAt);
      let detailTitle = 'Payment failed';
      let detailBody = `We’ll retry ${card}${graceEnds ? ` until ${graceEnds}` : ''}. Until then your benefits `
        + 'are paused — registering and ordering bags unlock again as soon as the payment goes through.';
      if (update === PAYMENT_UPDATE_STATUS.DECLINED) {
        detailTitle = 'New payment method declined';
        detailBody = `The payment method you just saved was declined. We’ll keep retrying${graceEnds ?
          ` until ${graceEnds}` : ''}, or you can try a different one. Until then your benefits are paused.`;
      } else if (update === PAYMENT_UPDATE_STATUS.SKIPPED) {
        detailTitle = 'New payment method saved';
        detailBody = `We’ll use ${card} for your next renewal attempt${next ? ` on ${next}` : ''}. Until then `
          + 'your benefits are paused — they unlock again as soon as the payment goes through.';
      }
      return {
        statusLabel: 'Payment past due',
        tone: RENEWAL_TONES.ALERT,
        summary: `We couldn’t process your ${amount ? `${amount} ` : ''}Unlimited renewal`
          + `${renewal ? ` on ${renewal}` : ''}.`,
        detailTitle,
        detailBody,
        footnote: last4 ?
          'Update your card to keep your membership active.' :
          'Update your payment method to keep your membership active.',
        autoRenewOn: true,
      };
    }
    case RENEWAL_STATES.ENDING: {
      const endsOn = formatLongDate(status.endsAt);
      let pitch = 'turn auto-renewal back on to keep it going.';
      if (offer?.regular && offer?.discounted) {
        pitch = `turn auto-renewal back on and save ${offer.off} on year two: ${offer.discounted} instead of ${offer.regular}.`;
      } else if (offer) {
        pitch = `turn auto-renewal back on and save ${offer.off} on year two.`;
      }
      const endsIn = hasDays(status.daysRemaining) ?
        `Your membership ends in ${pluralDays(status.daysRemaining)}.` :
        'Your membership ends soon.';
      let footnote = endsOn ? `Your membership will be ending on ${endsOn}.` : null;
      if (offer) {
        footnote = endsOn ?
          `${footnote} Renew by then to keep your ${offer.off} discount.` :
          `Renew before your membership ends to keep your ${offer.off} discount.`;
      }
      // A save / resume already redeemed this period (no new offer can stack on it)
      // stays on the renewal if they turn auto-renew back on.
      const appliedOff = offer ? null : formatCents(status.appliedDiscount?.amountOffCents);
      if (appliedOff) {
        const keepIt = `You already have ${appliedOff} off your next year. Turn auto-renew back on to keep it.`;
        footnote = footnote ? `${footnote} ${keepIt}` : keepIt;
      }
      return {
        statusLabel: endsOn ? `Ends on ${formatShortDate(status.endsAt)}` : 'Ending soon',
        tone: RENEWAL_TONES.OK,
        summary: endsOn ?
          `Your Unlimited membership will end on ${endsOn}` :
          'Your Unlimited membership will end soon',
        detailTitle: null,
        detailBody: `${endsIn} You’ve done so much work and created so much impact — ${pitch}`,
        footnote,
        ctaLabel: (offer && `Switch It On And Save ${offer.off}`)
          || (appliedOff && `Turn On Auto-Renew And Keep ${appliedOff} Off`)
          || 'Turn On Auto-Renew',
        autoRenewOn: false,
      };
    }
    case RENEWAL_STATES.ACTIVE: {
      // A save / resume offer redeemed this period: amountCents is already the
      // discounted renewal; say what came off.
      const appliedOff = formatCents(status.appliedDiscount?.amountOffCents);
      const renewsOn = formatLongDate(status.renewalDate);
      return {
        statusLabel: renewsOn ? `Renews on ${formatShortDate(status.renewalDate)}` : 'Auto-renew on',
        tone: RENEWAL_TONES.OK,
        summary: `Your Unlimited membership ${renewsOn ? `renews on ${renewsOn}` : 'renews automatically'}`
          + `${amount ? ` for ${amount}` : ''}${amount && appliedOff ? ` (${appliedOff} off)` : ''}.`,
        detailTitle: null,
        detailBody: null,
        footnote: null,
        autoRenewOn: true,
      };
    }
    default:
      return null;
  }
};

/**
 * sessionStorage key for "this member already saw the entry sheet for this state".
 * Keyed by user AND state so a member who goes from ENDING to LAPSED is shown the
 * new message, and a different account on the same tab is not suppressed.
 *
 * @param {string} uid
 * @param {object} status
 * @return {string}
 */
export const getEntrySheetSessionKey = (uid, status) => `trashie:renewalSheet:${uid}:${status?.state}`;

// The pre-expiry sheet repeats across app opens rather than once per session
// (Figma dev note: "Show this 30 days before the renewal date. Every 2 app opens
// after close"): shown, then skipped for one open, then shown again.
export const ENTRY_SHEET_REPEAT_EVERY_OPENS = Object.freeze({
  [RENEWAL_STATES.ENDING]: 2,
});

/**
 * localStorage key counting this member's app opens (one per browser session).
 *
 * @param {string} uid
 * @return {string}
 */
export const getAppOpensKey = (uid) => `trashie:appOpens:${uid}`;

/**
 * localStorage key holding the app-open number on which a repeating entry sheet
 * was last shown.
 *
 * @param {string} uid
 * @param {object} status
 * @return {string}
 */
export const getEntrySheetLastShownKey = (uid, status) => `trashie:renewalSheetShownAt:${uid}:${status?.state}`;

/**
 * Whether the entry sheet is due on this app open.
 *
 * Repeating states (ENDING) are shown on the first open and then every Nth open
 * after the one they were last shown on. Every other state is once per session,
 * which is the PRD's "every login" for the grace and lapsed banners.
 *
 * @param {object} params
 * @param {object} params.status - renewal-status payload
 * @param {boolean} params.shownThisSession
 * @param {number} params.appOpens - this member's app-open count, including this one
 * @param {number|null} params.lastShownOpen - app open it was last shown on, or null
 * @return {boolean}
 */
export const isEntrySheetDue = ({
  status, shownThisSession, appOpens, lastShownOpen,
}) => {
  if (shownThisSession) return false;
  const every = ENTRY_SHEET_REPEAT_EVERY_OPENS[status?.state];
  if (!every || !Number.isFinite(lastShownOpen)) return true;
  return appOpens - lastShownOpen >= every;
};
